import { DEX_TOKEN } from "../config/dex-token";
import { ELYSIUM } from "../config/elysium";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { terminalChains } from "../config/chains";
import { useLiveRuntime } from "./live-runtime";
import { useAppBootstrap } from "./use-app-bootstrap";
import { useMiningLifecycle, useMiningActions, useMiningDifficulty, miningView } from "../features/mining/runtime";
import { switchChain } from "../wallet/eip1193";
import { useWalletOperations } from "./use-wallet-operations";
import { sameAddress } from "./model-helpers";
import { inventoryView, useInventoryBrowser, useNftRead, useFinalizeNft,
  useRedeemNft, useRedeemPreview, useRedeemFeeRoute, useMintNft } from "../features/nft/runtime";
import { walletView } from "./view-mappers";
import { dashboardView, useDashboardReads } from "../features/dashboard/runtime";
import { useProofEligibility } from "./use-proof-eligibility";
import { proofContextMatches, proofLifecycleKey } from "./mint-eligibility";
import { actionPermissions, mintPreparationPermission, taskMintSubmissionPermission } from "./transaction-capability";
import { submitTaskMint as executeTaskMint } from "../features/social-tasks/runtime";
import type { XMintAuthorization } from "../features/social-tasks/api/social-tasks-schemas";
import { useTransactions } from "./use-transactions";
import type { EffectsLevel, TerminalAppModel } from "./ui-types";
import type { RedeemFeeRoute } from "../wallet/redeem-fee-quote";
import { referralSubmissionReason, submitReferralMint as executeReferralMint, useReferralMintEvidence } from "../features/referrals/runtime";
import type { ReferralAuthorization, ReferralMilestone } from "../features/referrals/api/referral-schemas";
import type { ReferralMintEvidence } from "../features/referrals/contracts/referral-mint";
import { readOptionalPreference, writeOptionalPreference } from "./optional-preferences";
import { matchesTransactionScope, transactionUnresolved } from "../wallet/transaction-tracker";
import { SwapTracker } from "../wallet/swap-tracker";
import { isElysium } from "../config/elysium";
import { inheritResponseTime } from "../api/response-time";
import { observeTradeWallet, useTradeBalances } from "../features/trade/state/use-trade-balances";
import { tradeConfigKey } from "../config/trade";
import { merkleV4ConfigFromBootstrap } from "../config/merkle-v4";
import { configuredElysiumRpcUrl } from "../wallet/elysium-rpc";
import { mintProofKey } from "../contracts/pons-reward-nft";

// Preserve the existing export path for consumers while keeping policy independent
// of React orchestration and controller initialization.
export { mintTransactionExecutionEnabled, walletTransactionExecutionEnabled } from "./transaction-capability";

export function useTerminalAppModel({ redeemFeeRoute = null, referralMintEvidence: injectedReferralEvidence = null, terminalActive = true, tradeActive = false }: {
  redeemFeeRoute?: RedeemFeeRoute | null; referralMintEvidence?: ReferralMintEvidence | null; terminalActive?: boolean; tradeActive?: boolean;
} = {}): TerminalAppModel {
  const { walletController, walletConnection, miningController, miningAudio, tasksReadCache, balanceQueryClient, tradeQuoteCache, bridgeHistory } = useLiveRuntime();
  const walletSelection = useSyncExternalStore(walletConnection.subscribe, walletConnection.getSnapshot, walletConnection.getSnapshot);
  const soundEnabled = useSyncExternalStore(miningAudio.subscribe, miningAudio.getEnabled, miningAudio.getEnabled);
  const walletSnapshot = useSyncExternalStore(walletController.subscribe, walletController.getSnapshot, walletController.getSnapshot);
  const tradeWalletObserver = useMemo(() => observeTradeWallet(walletController), [walletController]);
  const tradeWallet = useSyncExternalStore(tradeWalletObserver.subscribe, tradeWalletObserver.getSnapshot, tradeWalletObserver.getSnapshot);
  const miningSnapshot = useMiningLifecycle(miningController);
  const [activeAddress, setActiveAddress] = useState<string | null>(() => readOptionalPreference("greenwood.source-token"));
  const [refreshRevision, setRefreshRevision] = useState(0);
  const [swapTracker] = useState(() => new SwapTracker(sessionStorage));
  const [effects, setEffectsState] = useState<EffectsLevel>(() => readEffectsPreference());
  const activeContext = useRef<{ account: string | null; token: string | null; scopeKey: string }>({ account: null, token: null, scopeKey: "" });
  const tokenRevision = useRef(0);
  const readContextKey = useCallback(() => {
    const wallet = walletController.getSnapshot();
    return JSON.stringify([activeContext.current.scopeKey, walletController.getRevision(), wallet.account,
      wallet.chainId, wallet.authenticated]);
  }, [walletController]);
  const readPublicContextKey = useCallback(() => activeContext.current.scopeKey, []);

  const { bootstrap, bootstrapError } = useAppBootstrap({ walletController, activeAddress,
    setActiveAddress, tokenRevision, refreshRevision });
  const candidateTradeConfig = useMemo(() => merkleV4ConfigFromBootstrap(bootstrap), [bootstrap]);
  const tradeKey = candidateTradeConfig ? tradeConfigKey(candidateTradeConfig) : "unavailable";
  const stableTradeConfig = useRef(candidateTradeConfig);
  const tradeGeneration = useRef({ key: tradeKey, revision: 0 });
  if (tradeGeneration.current.key !== tradeKey) {
    stableTradeConfig.current = candidateTradeConfig;
    tradeGeneration.current = { key: tradeKey, revision: tradeGeneration.current.revision + 1 };
  }
  const tradeConfig = stableTradeConfig.current;
  const readTradeContextKey = useCallback(() => {
    const current = tradeWalletObserver.getSnapshot();
    return JSON.stringify([tradeGeneration.current, configuredElysiumRpcUrl()?.href,
      current.revision, current.identity, current.account, current.chainId]);
  }, [tradeWalletObserver]);
  const tradeContextKey = readTradeContextKey();

  const chains = terminalChains(bootstrap);
  const activeToken = bootstrap?.tokens.find((token) => sameAddress(token.tokenAddress, activeAddress)) ?? null;
  const scopeKey = JSON.stringify([activeAddress, tokenRevision.current, bootstrap?.contracts,
    bootstrap?.capabilities, bootstrap?.deployment, bootstrap?.sourceChainId, bootstrap?.environment, bootstrap?.forkTestOnly,
    activeToken, redeemFeeRoute]);
  activeContext.current = { account: walletSnapshot.account, token: activeToken?.tokenAddress ?? null, scopeKey };
  const contextKey = readContextKey();
  const balanceRead = useTradeBalances({ wallet: walletController, tracker: swapTracker, trade: tradeConfig }, balanceQueryClient, tradeActive);
  useLayoutEffect(() => { tasksReadCache?.syncContext(contextKey); }, [tasksReadCache, contextKey]);
  useLayoutEffect(() => { tradeQuoteCache.syncContext(tradeContextKey); }, [tradeQuoteCache, tradeContextKey, tradeWallet]);
  useLayoutEffect(() => { miningAudio.checkContext(); }, [miningAudio, contextKey]);
  const reads = useDashboardReads({ api: walletController.api, bootstrap, bootstrapError,
    tokenAddress: activeToken?.tokenAddress, walletSnapshot, miningSnapshot, contextKey, readContextKey,
    publicContextKey: scopeKey, readPublicContextKey, refreshRevision, terminalActive });
  const globalDifficultyBits = useMiningDifficulty(walletController.api, Boolean(bootstrap) && terminalActive,
    scopeKey, readPublicContextKey, bootstrap?.deployment.instanceId, refreshRevision);
  const mintBootstrap = useMemo(() => {
    if (!bootstrap || !isElysium(bootstrap) || !reads.fee) return bootstrap;
    const current = { ...bootstrap, mintState: reads.fee };
    inheritResponseTime(reads.fee, current);
    return current;
  }, [bootstrap, reads.fee]);
  const latestMintBootstrap = useRef(mintBootstrap);
  latestMintBootstrap.current = mintBootstrap;
  const proofMatches = proofContextMatches(bootstrap, miningSnapshot, walletSnapshot.authenticated, walletSnapshot.account, activeToken);
  const readProofIdentity = useCallback(() => proofLifecycleKey(miningController.getSnapshot()), [miningController]);
  const proofEligibility = useProofEligibility({ api: walletController.api, contextKey, readContextKey,
    subscribeProof: miningController.subscribe, readProofIdentity,
    enabled: proofMatches, refreshRevision, context: { matches: proofMatches,
      proofId: miningSnapshot.proof?.proofId ?? "", challengeId: miningSnapshot.challenge?.challengeId ?? "",
      permission: mintPreparationPermission(bootstrap, reads.fee) } });
  const { nftRead, inspectNft, refreshNft } = useNftRead({ api: walletController.api, bootstrap,
    sourceToken: activeToken?.tokenAddress ?? null, contextKey, readContextKey,
    provider: walletController.getProvider(), refreshRevision });
  const inventoryBrowser = useInventoryBrowser(walletController.api, contextKey,
    Boolean(bootstrap && walletSnapshot.authenticated), activeToken?.tokenAddress ?? null,
    walletSnapshot.account, miningSnapshot.phase === "RUNNING" ? 5_000 : 12_000, refreshRevision, readContextKey, terminalActive,
    bootstrap?.contracts.mintChainId, bootstrap?.contracts.collectionAddress);
  const { beginOperation, endOperation, cancelOperations } = useWalletOperations({
    walletController, activeContext, scopeKey });

  useEffect(() => {
    if (miningSnapshot.phase === "VERIFIED") setRefreshRevision((value) => value + 1);
  }, [miningSnapshot.phase]);

  const refresh = useCallback(() => setRefreshRevision((value) => value + 1), []);
  const transactionEnvironment = bootstrap ? `${bootstrap.environment}:${bootstrap.capabilities.mintGatewayMode}` : "";
  const transactions = useTransactions(walletSnapshot.account && bootstrap?.contracts.collectionAddress &&
    bootstrap.contracts.mintChainId ? {
      account: walletSnapshot.account, chainId: bootstrap.contracts.mintChainId,
      collection: bootstrap.contracts.collectionAddress, environment: transactionEnvironment,
    } : null, walletSnapshot.chainId, walletController.getProvider(), refresh);

  const isMiningTransactionBlocked = useCallback(() => {
    const account = walletController.getSnapshot().account;
    const chainId = bootstrap?.contracts.mintChainId;
    const collection = bootstrap?.contracts.collectionAddress;
    if (!account || !chainId || !collection) return false;
    const scope = { account, chainId, collection, environment: transactionEnvironment };
    return transactions.tracker.getSnapshot().items.some((item) => matchesTransactionScope(item, scope) && transactionUnresolved(item));
  }, [walletController, bootstrap, transactionEnvironment, transactions.tracker]);

  useEffect(() => {
    if (contextKey !== readContextKey() || isMiningTransactionBlocked() ||
      !proofEligibility.retirementStatus || !proofEligibility.proofId) return;
    miningController.retireProof(proofEligibility.proofId, proofEligibility.retirementStatus);
  }, [contextKey, readContextKey, transactions.view.blocked, proofEligibility.retirementStatus,
    proofEligibility.proofId, miningController, isMiningTransactionBlocked]);

  const pendingWalletLogin = useRef<Promise<void> | null>(null);
  const connectWallet = useCallback((walletId?: string): Promise<void> => {
    if (pendingWalletLogin.current) return pendingWalletLogin.current;
    const initial = walletController.getSnapshot();
    const initialRevision = walletController.getRevision();
    if (initial.status === "CONNECTING" || initial.status === "SIGNING_IN") return Promise.resolve();
    const siweRevision = walletController.getSiweRevision();
    // Capture the first adopted wallet, then fence all later context changes,
    // including A -> B -> A while the connector is still resolving.
    let connectedRevision = initial.connected ? walletController.getRevision() : null;
    const unsubscribe = walletController.subscribe(() => {
      if (connectedRevision === null && walletController.getSnapshot().connected) {
        connectedRevision = walletController.getRevision();
      }
    });
    const work = Promise.resolve().then(async () => {
      if (initialRevision !== walletController.getRevision() || siweRevision !== walletController.getSiweRevision()) return;
      if (!initial.connected) await walletConnection.connect(walletId);
      const current = walletController.getSnapshot();
      if (!current.connected || connectedRevision !== walletController.getRevision() ||
        siweRevision !== walletController.getSiweRevision() || current.authenticated) return;
      // An unavailable configuration or wrong network leaves an explicit page
      // action. Configuration/network changes never trigger a signing effect.
      if (!bootstrap || bootstrapError || current.chainId !== bootstrap.siwe.chainId) return;
      await walletController.authenticate();
    }).finally(() => {
      unsubscribe();
      if (pendingWalletLogin.current === work) pendingWalletLogin.current = null;
    });
    pendingWalletLogin.current = work;
    return work;
  }, [walletController, walletConnection, bootstrap, bootstrapError]);

  const selectSourceToken = useCallback(async (address: string) => {
    if (!bootstrap?.tokens.some((token) => sameAddress(token.tokenAddress, address))) return;
    if (sameAddress(activeContext.current.token, address)) return;
    const miningPhase = miningController.getSnapshot().phase;
    if (["CANDIDATE_LOCAL", "SUBMITTING", "SUBMIT_RETRY"].includes(miningPhase)) {
      throw new Error("Wait for the current proof submission to settle before switching source token");
    }
    if (["RUNNING", "ACQUIRING_TAB_LOCK"].includes(miningPhase)) {
      const approved = globalThis.confirm?.("Pause the current challenge and switch source token?") ?? false;
      if (!approved) return;
      await miningController.pause("SOURCE_TOKEN_CHANGED");
    }
    writeOptionalPreference("greenwood.source-token", address);
    tokenRevision.current += 1;
    miningAudio.cancel();
    activeContext.current = { ...activeContext.current, token: address, scopeKey: `selection:${tokenRevision.current}` };
    cancelOperations();
    setActiveAddress(address);
    setRefreshRevision((value) => value + 1);
  }, [bootstrap, miningController, miningAudio, cancelOperations]);

  const actionContext = { contextKey, readContextKey, activeContext, beginOperation, endOperation };
  const resolvedRedeem = useRedeemFeeRoute({ walletController, walletSnapshot, bootstrap,
    sourceToken: activeToken?.tokenAddress ?? null, injectedRoute: redeemFeeRoute,
    contextKey, readContextKey, refreshRevision });
  const resolvedReferral = useReferralMintEvidence({ walletController, walletSnapshot, bootstrap,
    sourceToken: activeToken?.tokenAddress ?? null, injectedEvidence: injectedReferralEvidence,
    contextKey, readContextKey, refreshRevision });
  const referralMintEvidence = resolvedReferral.evidence;
  const submitTaskMint = useCallback((authorization: XMintAuthorization, assertCurrent: () => void) =>
    executeTaskMint(authorization, assertCurrent, { bootstrap: mintBootstrap, sourceToken: activeToken?.tokenAddress ?? null,
      wallet: walletController, tracker: transactions.tracker, environment: transactionEnvironment, beginOperation, endOperation }),
  [mintBootstrap, activeToken, walletController, transactions.tracker, transactionEnvironment, beginOperation, endOperation]);
  const switchTaskMintNetwork = useCallback(async () => {
    if (!bootstrap?.contracts.mintChainId) throw new Error("Task network unavailable");
    const operation = beginOperation();
    try { await operation.switchTo(bootstrap.contracts.mintChainId); }
    finally { endOperation(operation); }
  }, [bootstrap, beginOperation, endOperation]);
  const submitReferralMint = useCallback((authorization: ReferralAuthorization, assertCurrent: () => void) =>
    executeReferralMint(authorization, assertCurrent, { bootstrap: mintBootstrap, sourceToken: activeToken?.tokenAddress ?? null,
      getBootstrap: () => latestMintBootstrap.current,
      evidence: referralMintEvidence, wallet: walletController, tracker: transactions.tracker,
      environment: transactionEnvironment, beginOperation, endOperation }),
  [mintBootstrap, activeToken, referralMintEvidence, walletController, transactions.tracker, transactionEnvironment, beginOperation, endOperation]);
  const recordReferralClaim = useCallback((milestone: ReferralMilestone) => {
    const account = walletController.getSnapshot().account;
    if (account && bootstrap?.contracts.mintChainId && bootstrap.contracts.collectionAddress) {
      transactions.tracker.recordReferralClaim({ account, chainId: bootstrap.contracts.mintChainId,
        collection: bootstrap.contracts.collectionAddress, environment: transactionEnvironment }, milestone);
    }
  }, [walletController, bootstrap, transactions.tracker, transactionEnvironment]);
  const { finalizeNft, finalizeNotice } = useFinalizeNft({ ...actionContext, walletController, bootstrap,
    tracker: transactions.tracker, transactionEnvironment, refresh });
  const { startMining, retryMiningSubmission, resumeMining, miningStarting, authExpiring,
    checkMiningStatus, startNewMiningSession, checkSavedMiningProof,
    setMiningMode, setMiningPowerMode, pauseMining, stopMining } = useMiningActions({
    ...actionContext, walletController, walletSnapshot, miningController, miningAudio, bootstrap, activeToken,
    isTransactionBlocked: isMiningTransactionBlocked,
  });
  const { prepareMint, confirmMint, cancelMintReview, mintReview } = useMintNft({
    ...actionContext, walletController, miningController, bootstrap, activeToken,
    mintState: reads.fee,
    tracker: transactions.tracker, transactionEnvironment,
  });
  const { prepareRedeem, confirmRedeem, cancelRedeemReview, redeemReview, redeemNft } = useRedeemNft({
    ...actionContext, walletController, bootstrap, tracker: transactions.tracker, transactionEnvironment, refresh,
    feeRoute: resolvedRedeem.route, feeRouteUnavailable: resolvedRedeem.unavailable,
  });
  const { redeemPreviews, requestRedeemPreview } = useRedeemPreview({
    ...actionContext, walletController, walletSnapshot, inventory: inventoryBrowser.data,
    inventoryRevision: inventoryBrowser.revision,
    bootstrap, feeRoute: resolvedRedeem.route, feeRouteUnavailable: resolvedRedeem.unavailable,
  });

  const { projectContractsMissing, ...dashboard } = dashboardView(bootstrap, bootstrapError, reads, walletSnapshot.account);
  const sourceTokens = useMemo(() => bootstrap?.tokens.map((token) => {
    const project = token.chainId === ELYSIUM.chainId && sameAddress(token.tokenAddress, tradeConfig?.token ?? ELYSIUM.token);
    return { address: token.tokenAddress, symbol: project ? DEX_TOKEN.symbol : token.symbol,
      name: project ? DEX_TOKEN.name : token.name, chainId: token.chainId, active: token.tracking };
  }) ?? [], [bootstrap?.tokens, tradeConfig]);
  const inventory = useMemo(() => ({ ...inventoryView(inventoryBrowser.data, sourceTokens, walletSnapshot.account,
    inventoryBrowser.loading && inventoryBrowser.data === null,
    inventoryBrowser.loadingMore, redeemPreviews, projectContractsMissing, !bootstrap && bootstrapError !== null,
    inventoryBrowser.error ?? (!walletSnapshot.account ? "WALLET_NOT_CONNECTED" :
      !walletSnapshot.authenticated ? "SIGN_IN_REQUIRED" : null)),
    newSnapshot: inventoryBrowser.newSnapshot,
    updateError: inventoryBrowser.data?.inventoryAvailable ? inventoryBrowser.error : null,
    refreshing: inventoryBrowser.loading, revision: inventoryBrowser.revision,
  }), [inventoryBrowser.data, inventoryBrowser.loading, inventoryBrowser.loadingMore, inventoryBrowser.error,
    inventoryBrowser.newSnapshot, inventoryBrowser.revision, sourceTokens,
    walletSnapshot.account, walletSnapshot.authenticated, redeemPreviews, projectContractsMissing, bootstrap, bootstrapError]);
  const mining = miningView(miningSnapshot, actionPermissions(bootstrap).mining.state === "ALLOWED",
    walletSnapshot, activeToken?.tokenAddress ?? null, globalDifficultyBits);
  mining.newChallengeBlocked = transactions.view.blocked;
  const challenge = miningSnapshot.challenge;
  if (mining.phase === "VERIFIED" && challenge) {
    const proofKey = mintProofKey({ instanceId: challenge.instanceId, chainId: BigInt(challenge.chainId),
      challengeId: challenge.challengeId, challengeCommitment: challenge.challengeCommitment });
    const lastMint = transactions.view.items.find(item => item.kind === "MINT" &&
      !item.mintIdentity?.route && sameAddress(item.mintIdentity?.proofKey ?? "", proofKey));
    mining.mintRetryAvailable = !!lastMint && ["REVERTED", "FAILED", "REJECTED", "CANCELLED"].includes(lastMint.phase);
  }
  mining.authExpiring = authExpiring;
  mining.canRestoreCheckpoint = Boolean(bootstrap && walletSnapshot.authenticated && activeToken &&
    (!miningSnapshot.challenge || mining.phase === "LOCKED"));
  if (miningStarting) {
    mining.phase = "STARTING";
    mining.statusMessage = "Preparing the authenticated mining challenge";
  }
  return {
    mintHistory: bootstrap?.contracts.mintChainId && bootstrap.contracts.collectionAddress ? {
      api: walletController.api, chainId: bootstrap.contracts.mintChainId,
      collectionAddress: bootstrap.contracts.collectionAddress,
      environment: `${bootstrap.environment}:${bootstrap.forkTestOnly}:${bootstrap.capabilities.mintGatewayMode}`,
    } : undefined,
    bridge: { wallet: walletController, openWalletSelection: walletConnection.open, history: bridgeHistory },
    trade: { wallet: walletController, tracker: swapTracker, trade: tradeConfig, balanceRead, quoteCache: tradeQuoteCache,
      configurationMessage: bootstrapError ? "Trade configuration could not be verified. Retry to continue." :
        bootstrap?.trade?.configurationStatus === "INCOMPLETE" ? "Trade configuration is incomplete. Swaps are unavailable." :
          !bootstrap ? "Loading trade configuration…" : "Trade configuration is unavailable.",
      openWalletSelection: walletConnection.open,
      contextKey: tradeContextKey, readContextKey: readTradeContextKey, refresh },
    miningTelemetry: miningController,
    referralMintDeployment: { chainId: bootstrap?.contracts.mintChainId ?? null,
      collectionAddress: bootstrap?.contracts.collectionAddress ?? null, sourceToken: activeToken?.tokenAddress ?? null,
      evidence: referralMintEvidence, submissionReason: resolvedReferral.reason || referralSubmissionReason(mintBootstrap, activeToken?.tokenAddress ?? null,
        referralMintEvidence, transactionEnvironment) || transactions.view.storageWarning || transactions.view.referralClaimStorageWarning },
    taskMintDeployment: { chainId: bootstrap?.contracts.mintChainId ?? null,
      collectionAddress: bootstrap?.contracts.collectionAddress ?? null, sourceToken: activeToken?.tokenAddress ?? null,
      submissionReason: taskMintSubmissionPermission(mintBootstrap).reasons.join(", ") ||
        (transactions.view.storageWarning || transactions.view.socialClaimStorageWarning ? "Task recovery storage unavailable" : null) ||
        (!activeToken?.tracking ? "Registered task source unavailable" : null) },
    proofEligibility,
    mintReview,
    redeemReview,
    finalizeNotice,
    contextKey,
    brandName: import.meta.env.VITE_BRAND_NAME || "MERKLEWOOD",
    transactions: transactions.view,
    sourceTokens,
    activeSourceToken: activeToken ? { address: activeToken.tokenAddress, symbol: activeToken.symbol,
      name: activeToken.name, chainId: activeToken.chainId, active: activeToken.tracking } : null,
    wallet: walletView(walletSnapshot, chains.source, bootstrapError ? "ERROR" : bootstrap ? "READY" : "LOADING"),
    walletSelection,
    mining,
    inventory,
    nftRead,
    ...dashboard,
    effects,
    sound: { enabled: soundEnabled, setEnabled: miningAudio.setEnabled },
    setEffects(level) {
      setEffectsState(level);
      writeOptionalPreference("greenwood.effects", level);
    },
    actions: {
      connectWallet,
      connectWalletOnly: walletConnection.connect,
      openWalletSelection: walletConnection.open,
      closeWalletSelection: walletConnection.close,
      disconnectWallet: walletConnection.disconnect,
      checkWalletSession: () => walletController.checkSession(),
      retryWalletLogout: () => walletController.logout(),
      switchNetwork: async () => {
        const provider = walletController.getProvider();
        if (!provider) throw new Error("Connect a wallet first");
        const identity = walletController.getIdentityRevision(), siwe = walletController.getSiweRevision();
        await switchChain(provider, chains.siwe, () => {
          if (walletController.getProvider() !== provider || walletController.getIdentityRevision() !== identity ||
            walletController.getSiweRevision() !== siwe)
            throw new Error("Wallet or sign-in configuration changed. Try switching the current wallet again.");
        });
      },
      selectSourceToken,
      setMiningMode,
      setMiningPowerMode,
      startMining,
      checkMiningStatus, startNewMiningSession, checkSavedMiningProof,
      pauseMining,
      resumeMining,
      retryMiningSubmission,
      stopMining,
      refresh,
      loadMoreNfts: inventoryBrowser.loadMore,
      refreshInventory: inventoryBrowser.refresh,
      requestRedeemPreview,
      inspectNft,
      refreshNft,
      finalizeNft,
      prepareMint,
      confirmMint,
      cancelMintReview,
      submitTaskMint,
      submitReferralMint,
      recordReferralClaim,
      switchReferralMintNetwork: switchTaskMintNetwork,
      switchTaskMintNetwork,
      redeemNft,
      prepareRedeem,
      confirmRedeem,
      cancelRedeemReview,
      checkTransaction: transactions.checkTransaction,
      dismissTransaction: transactions.dismissTransaction,
    },
  };
}

function readEffectsPreference(): EffectsLevel {
  const stored = readOptionalPreference("greenwood.effects");
  if (stored === "ON" || stored === "LOW" || stored === "OFF") return stored;
  return matchMedia("(prefers-reduced-motion: reduce)").matches ? "OFF" : "ON";
}
